import { useEffect } from 'react';

export default function Anniversary30Page() {
  useEffect(() => {
    // Scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.fade-in-up, .fade-in, .rotate-in, .fade-in-left, .fade-in-right').forEach(el => {
      observer.observe(el);
    });

    // Gallery functionality
    const setupGallery = () => {
      const thumbs = document.querySelectorAll('.evtgal-thumb');
      const lightbox = document.getElementById('evtgal-lightbox');
      const bigImg = document.getElementById('evtgal-big') as HTMLImageElement;
      const closeBtn = document.getElementById('evtgal-close');
      const prevBtn = document.getElementById('evtgal-prev');
      const nextBtn = document.getElementById('evtgal-next');
      const indexEl = document.getElementById('evtgal-index');
      const dots = document.querySelectorAll('.evtgal-dot');
      
      let currentIndex = 0;

      const showImage = (index: number) => {
        currentIndex = index;
        const thumb = thumbs[index] as HTMLImageElement;
        if (bigImg && thumb) {
          bigImg.src = thumb.src;
        }
        if (indexEl) {
          indexEl.textContent = `${index + 1}/${thumbs.length}`;
        }
        dots.forEach((dot, i) => {
          dot.classList.toggle('active', i === index);
        });
      };

      thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => {
          lightbox?.classList.add('active');
          showImage(index);
        });
      });

      closeBtn?.addEventListener('click', () => {
        lightbox?.classList.remove('active');
      });

      prevBtn?.addEventListener('click', () => {
        const newIndex = currentIndex > 0 ? currentIndex - 1 : thumbs.length - 1;
        showImage(newIndex);
      });

      nextBtn?.addEventListener('click', () => {
        const newIndex = currentIndex < thumbs.length - 1 ? currentIndex + 1 : 0;
        showImage(newIndex);
      });

      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          showImage(index);
        });
      });

      lightbox?.addEventListener('click', (e) => {
        if (e.target === lightbox) {
          lightbox.classList.remove('active');
        }
      });
    };

    setupGallery();

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 via-purple-50 to-pink-50">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@400;600;700&family=Shippori+Mincho:wght@400;600;700&display=swap');
        
        body {
          font-family: 'Noto Serif JP', serif;
        }

        .expand-text {
          position: absolute;
          left: -9999px;
          opacity: 0;
        }

        .shiny {
          animation: shine 3s ease-in-out infinite;
        }

        @keyframes shine {
          0%, 100% { filter: brightness(1); }
          50% { filter: brightness(1.3) drop-shadow(0 0 20px rgba(255,255,255,0.8)); }
        }

        .water-drop {
          animation: waterDrop 1s ease-out;
        }

        @keyframes waterDrop {
          0% { transform: translateY(-30px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }

        .bouncy {
          display: inline-block;
          animation: bounce 2s ease-in-out infinite;
        }

        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        .fade-in-up {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease-out;
        }

        .fade-in {
          opacity: 0;
          transition: opacity 1s ease-out;
        }

        .rotate-in {
          opacity: 0;
          transform: rotate(-180deg) scale(0.5);
          transition: all 0.8s ease-out;
        }

        .fade-in-left {
          opacity: 0;
          transform: translateX(-50px);
          transition: all 0.8s ease-out;
        }

        .fade-in-right {
          opacity: 0;
          transform: translateX(50px);
          transition: all 0.8s ease-out;
        }

        .animate-in {
          opacity: 1 !important;
          transform: translateY(0) translateX(0) rotate(0) scale(1) !important;
        }

        .js-marker {
          background: linear-gradient(transparent 60%, #ffd700 60%);
          font-weight: 700;
          padding: 0 4px;
        }

        .background-fixed {
          background-image: url('https://dynavision.co.jp/30Anniversary/img/bg.jpg');
          background-attachment: fixed;
          background-size: cover;
          background-position: center;
          padding: 80px 20px;
        }

        /* Gallery Styles */
        .evtgal-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .evtgal-card {
          position: relative;
          overflow: hidden;
          border-radius: 12px;
          cursor: pointer;
          aspect-ratio: 1;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .evtgal-card:hover {
          transform: scale(1.05);
          box-shadow: 0 8px 24px rgba(0,0,0,0.2);
        }

        .evtgal-thumb {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .evtgal-lightbox {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0,0,0,0.95);
          z-index: 9999;
          align-items: center;
          justify-content: center;
        }

        .evtgal-lightbox.active {
          display: flex;
        }

        .evtgal-viewer {
          position: relative;
          max-width: 90%;
          max-height: 90%;
        }

        .evtgal-big {
          max-width: 100%;
          max-height: 90vh;
          object-fit: contain;
        }

        .evtgal-ctrl {
          position: absolute;
          top: 20px;
          right: 20px;
          display: flex;
          gap: 10px;
        }

        .evtgal-btn {
          background: rgba(255,255,255,0.9);
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .evtgal-btn:hover {
          background: white;
          transform: scale(1.05);
        }

        .evtgal-btn-round {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          font-size: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
        }

        .evtgal-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
        }

        .evtgal-nav-left {
          left: -70px;
        }

        .evtgal-nav-right {
          right: -70px;
        }

        .evtgal-index {
          position: absolute;
          bottom: 80px;
          left: 50%;
          transform: translateX(-50%);
          color: white;
          font-size: 18px;
          font-weight: 600;
        }

        .evtgal-dots {
          position: absolute;
          bottom: 30px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 10px;
        }

        .evtgal-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: rgba(255,255,255,0.5);
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .evtgal-dot.active {
          background: white;
          transform: scale(1.3);
        }

        @media (max-width: 768px) {
          .evtgal-nav-left { left: 10px; }
          .evtgal-nav-right { right: 10px; }
          .evtgal-ctrl { top: 10px; right: 10px; }
          .evtgal-btn { padding: 8px 16px; font-size: 14px; }
        }
      `}</style>

      {/* Hidden H1 for SEO */}
      <h1 className="expand-text">
        <span>あ</span><span>な</span><span>た</span><span>の</span>
        <span>無</span><span>限</span><span>の</span><span>可</span>
        <span>能</span><span>性</span><span>の</span><span>扉</span>
        <span>を</span><span>開</span><span>く</span>
      </h1>

      {/* Hero Section */}
      <div className="relative py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <img 
            src="https://dynavision.co.jp/30Anniversary/img/titleLP.png" 
            alt="ダイナビジョン 創立30周年記念パーティー ✨ ― 感謝と共鳴の式典 ―" 
            className="w-full fade-in-up"
          />
          <h2 className="text-2xl md:text-3xl mt-8 text-gray-700 water-drop font-semibold">
            150名限定のスペシャル祝典
          </h2>
        </div>
      </div>

      {/* CTA Button 1 */}
      <div className="background-fixed">
        <div className="text-center">
          <a 
            href="https://dynavisionasp.com/p/campaign/xC1bmkhA/sQ5m5w2R" 
            target="_blank" 
            rel="noopener noreferrer"
            className="bouncy inline-block"
          >
            <img 
              src="https://dynavision.co.jp/30Anniversary/img/moushi.jpg" 
              alt="お申し込みはこちら" 
              className="w-4/5 max-w-md mx-auto"
            />
          </a>
        </div>
      </div>

      {/* Founder Message */}
      <div className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white p-8 md:p-12 rounded-2xl shadow-lg">
            <div className="text-center mb-8">
              <img 
                src="https://dynavision.co.jp/30Anniversary/img/logo.png" 
                alt="ダイナビジョン" 
                className="w-32 mx-auto shiny mb-4"
              />
              <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
                創始者・穴口恵子より
              </h3>
              <h4 className="text-xl md:text-3xl text-gray-700 my-6 leading-relaxed">
                ― 30年の奇跡を、あなたと共に ―
              </h4>
            </div>

            <img 
              src="https://dynavision.co.jp/30Anniversary/img/keiko.png" 
              alt="穴口恵子" 
              className="w-full mb-8 fade-in rounded-lg"
            />

            <div className="text-left text-base md:text-lg leading-loose space-y-6">
              <p>皆さまへ</p>
              <p>いま、心の底からあふれる言葉は<br className="md:hidden" /><span className="js-marker">「ありがとう」</span> です。</p>
              <p>1995年。私と2名の仲間、たった3人で始めた小さな会社が、このたび、皆さまの愛に支えられて30周年という節目を迎えることができました。</p>
              <p>あの日、小さな部屋で願ったのは、「ひとりひとりの魂が輝く地球を創りたい」という、ただその一つの祈りでした。</p>
              <p>それからの年月、幾千もの出会いがあり、笑顔があり、涙があり、そして、数えきれない"奇跡"が起こりました。</p>
              <p>ダイナビジョンの30年は、私たちだけの歩みではなく、あなたという光が共に紡いでくれた「魂の物語」です。</p>
              <p>この30周年パーティーは、これまで共に歩んでくださった皆さまへの「感謝の祝宴」。そして、新しい時代へと進むための"共鳴の祈り"でもあります。</p>
              <p>スピリチュアル界で活躍する素晴らしい仲間たち、音楽やパフォーマンスで心を震わせるアーティストたちも、この夜、ひとつのステージに集います。</p>
              <p>笑いあり、涙あり、感動ありエンターテインメントと愛が融合した、魂の祝福の時間です。</p>
              <p>どうかあなたも、この奇跡の瞬間を共に感じてください。それが、私たちにとって最高の贈りものです。</p>
            </div>

            <div className="text-right mt-8 text-gray-700">
              <p className="text-lg">心からの愛と感謝を込めて</p>
              <p className="text-xl font-bold mt-2">創始者 穴口恵子</p>
            </div>
          </div>
        </div>
      </div>

      {/* Event Overview */}
      <div className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <img 
              src="https://dynavision.co.jp/30Anniversary/img/logo.png" 
              alt="ダイナビジョン" 
              className="w-32 mx-auto shiny mb-4"
            />
            <h3 className="text-2xl md:text-3xl font-bold text-gray-800">
              30周年記念 ― 感謝と共鳴の祝典
            </h3>
          </div>

          <div className="mb-12">
            <img 
              src="https://dynavision.co.jp/30Anniversary/img/img01.jpg" 
              alt="イベント会場" 
              className="w-full rounded-2xl shadow-lg fade-in-up"
            />
          </div>

          {/* Gallery */}
          <section className="evtgal-wrap mb-12">
            <div className="evtgal-grid" id="evtgal-grid">
              <div className="evtgal-card">
                <img className="evtgal-thumb" src="https://dynavision.co.jp/30Anniversary/img/slide06.jpg" alt="イベント写真" />
              </div>
              <div className="evtgal-card">
                <img className="evtgal-thumb" src="https://dynavision.co.jp/30Anniversary/img/slide02.jpg" alt="イベント写真" />
              </div>
              <div className="evtgal-card">
                <img className="evtgal-thumb" src="https://dynavision.co.jp/30Anniversary/img/slide08.jpg" alt="イベント写真" />
              </div>
              <div className="evtgal-card">
                <img className="evtgal-thumb" src="https://dynavision.co.jp/30Anniversary/img/slide05.jpg" alt="イベント写真" />
              </div>
              <div className="evtgal-card">
                <img className="evtgal-thumb" src="https://dynavision.co.jp/30Anniversary/img/slide04.jpg" alt="イベント写真" />
              </div>
              <div className="evtgal-card">
                <img className="evtgal-thumb" src="https://dynavision.co.jp/30Anniversary/img/slide11.jpg" alt="イベント写真" />
              </div>
              <div className="evtgal-card">
                <img className="evtgal-thumb" src="https://dynavision.co.jp/30Anniversary/img/slide03.jpg" alt="イベント写真" />
              </div>
              <div className="evtgal-card">
                <img className="evtgal-thumb" src="https://dynavision.co.jp/30Anniversary/img/slide10.jpg" alt="イベント写真" />
              </div>
              <div className="evtgal-card">
                <img className="evtgal-thumb" src="https://dynavision.co.jp/30Anniversary/img/slide01.jpg" alt="イベント写真" />
              </div>
              <div className="evtgal-card">
                <img className="evtgal-thumb" src="https://dynavision.co.jp/30Anniversary/img/slide12.jpg" alt="イベント写真" />
              </div>
              <div className="evtgal-card">
                <img className="evtgal-thumb" src="https://dynavision.co.jp/30Anniversary/img/slide07.jpg" alt="イベント写真" />
              </div>
              <div className="evtgal-card">
                <img className="evtgal-thumb" src="https://dynavision.co.jp/30Anniversary/img/slide09.jpg" alt="イベント写真" />
              </div>
            </div>

            {/* Lightbox */}
            <div className="evtgal-lightbox" id="evtgal-lightbox">
              <div className="evtgal-viewer">
                <img id="evtgal-big" className="evtgal-big" alt="拡大画像" />
                <div className="evtgal-ctrl">
                  <button className="evtgal-btn" id="evtgal-close">Close ✕</button>
                </div>
                <div className="evtgal-nav evtgal-nav-left">
                  <button className="evtgal-btn evtgal-btn-round" id="evtgal-prev">❮</button>
                </div>
                <div className="evtgal-nav evtgal-nav-right">
                  <button className="evtgal-btn evtgal-btn-round" id="evtgal-next">❯</button>
                </div>
                <div className="evtgal-index" id="evtgal-index">1/12</div>
                <div className="evtgal-dots" id="evtgal-dots">
                  {[...Array(12)].map((_, i) => (
                    <button key={i} className="evtgal-dot"></button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <div className="text-base md:text-lg leading-loose space-y-6 text-gray-700">
            <p>1995年の創業から30年。スピリチュアルという言葉がまだ馴染みのなかった時代に、ダイナビジョンは<strong className="js-marker">「魂の目覚め」「愛の循環」「共鳴の社会」</strong>を掲げて歩み始めました。</p>
            <p>これまで支えてくださった皆さま、共に成長してくださった仲間、愛をもって広げてくださったファミリーの皆さま──すべての存在があって、今のダイナビジョンがあります。</p>
            <p className="font-bold">この式典は、そんな<strong className="js-marker">"すべての魂への感謝"</strong>を込めた、特別な夜です。</p>
          </div>
        </div>
      </div>

      {/* Special Programs */}
      <div className="py-20 px-4 bg-white/50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <img 
              src="https://dynavision.co.jp/30Anniversary/img/logo.png" 
              alt="ダイナビジョン" 
              className="w-32 mx-auto shiny mb-4"
            />
            <h3 className="text-2xl md:text-3xl font-bold text-gray-800">
              スペシャルなプログラム
            </h3>
          </div>

          <div className="space-y-8">
            {[
              { num: 1, title: '豪華ゲストが登壇!', desc: '長年ダイナビジョンを支えてきたリーダーや講師、国内外で活躍するスピリチュアルアーティストたちが出演。' },
              { num: 2, title: '融合ステージ', desc: '30年の軌跡を振り返る映像、ライブ演出、サプライズ演目など、「感謝」と「光」で包まれる特別な夜。' },
              { num: 3, title: '東京・港区ラグジュアリーホテル', desc: '非日常の空間で、美味しいコースディナーとドリンクを味わいながら、心に残るひとときをお過ごしください。' },
              { num: 4, title: 'フォーマルな装い', desc: '特別な夜にふさわしいフォーマルまたはセミフォーマルな装いで、華やかな時間をお楽しみください。' }
            ].map((item, index) => (
              <div key={index} className="bg-gradient-to-r from-pink-50 to-purple-50 p-8 rounded-2xl shadow-md">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 flex-shrink-0 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center text-white text-2xl font-bold rotate-in shadow-lg">
                    {item.num}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-xl md:text-2xl font-bold text-gray-800 mb-3 fade-in-left">
                      {item.title}
                    </h4>
                    <p className="text-base md:text-lg text-gray-700 leading-relaxed fade-in-right">
                      <strong>{item.desc}</strong>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Button 2 */}
      <div className="background-fixed">
        <div className="text-center">
          <a 
            href="https://dynavisionasp.com/p/campaign/xC1bmkhA/sQ5m5w2R" 
            target="_blank" 
            rel="noopener noreferrer"
            className="bouncy inline-block"
          >
            <img 
              src="https://dynavision.co.jp/30Anniversary/img/moushi.jpg" 
              alt="お申し込みはこちら" 
              className="w-4/5 max-w-md mx-auto"
            />
          </a>
        </div>
      </div>

      {/* Ticket Plans */}
      <div className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <img 
              src="https://dynavision.co.jp/30Anniversary/img/logo.png" 
              alt="ダイナビジョン" 
              className="w-32 mx-auto shiny mb-4"
            />
            <h3 className="text-2xl md:text-3xl font-bold text-gray-800">
              チケットのご案内
            </h3>
          </div>

          {/* Regular Plans */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gradient-to-br from-pink-100 to-pink-50 p-8 rounded-2xl shadow-lg text-center fade-in-up">
              <h4 className="text-2xl font-bold text-pink-800 mb-4">感謝枠</h4>
              <div className="text-4xl font-bold text-pink-600 mb-2">15,000円<span className="text-lg text-gray-600">(税込)</span></div>
              <p className="text-gray-700 leading-relaxed">ご家族・ご夫婦・親子など、<br />お二人目以降の特別枠</p>
            </div>

            <div className="bg-gradient-to-br from-purple-100 to-purple-50 p-8 rounded-2xl shadow-lg text-center fade-in-up" style={{ animationDelay: '0.1s' }}>
              <h4 className="text-2xl font-bold text-purple-800 mb-4">スタンダード枠</h4>
              <div className="text-4xl font-bold text-purple-600 mb-2">30,000円<span className="text-lg text-gray-600">(税込)</span></div>
              <p className="text-gray-700 leading-relaxed">一般・関係者向け<br />(会場費・食事・ギフト含む)</p>
            </div>

            <div className="bg-gradient-to-br from-rose-100 to-rose-50 p-8 rounded-2xl shadow-lg text-center fade-in-up" style={{ animationDelay: '0.2s' }}>
              <h4 className="text-2xl font-bold text-rose-800 mb-4">スペシャル枠</h4>
              <div className="text-4xl font-bold text-rose-600 mb-2">50,000円<span className="text-lg text-gray-600">(税込)</span></div>
              <p className="text-gray-700 leading-relaxed">ご祝儀・応援参加者向け<br />(上位ギフト付)</p>
            </div>
          </div>

          {/* Sponsor Plans */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-amber-100 to-yellow-50 p-8 rounded-2xl shadow-xl text-center relative fade-in-up" style={{ animationDelay: '0.3s' }}>
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                PREMIUM SPONSOR
              </div>
              <h4 className="text-2xl font-bold text-amber-800 mb-4 mt-4">プレミアスポンサー枠</h4>
              <div className="text-4xl font-bold text-amber-600 mb-2">100,000円<span className="text-lg text-gray-600">(税込)</span></div>
              <p className="text-gray-700 leading-relaxed">協賛ボード掲載/特別席/記念撮影/限定ギフト付</p>
            </div>

            <div className="bg-gradient-to-br from-purple-200 to-pink-100 p-8 rounded-2xl shadow-xl text-center relative fade-in-up" style={{ animationDelay: '0.4s' }}>
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                MAIN SPONSOR
              </div>
              <h4 className="text-2xl font-bold text-purple-800 mb-4 mt-4">メインスポンサー枠<br />(限定2枠)</h4>
              <div className="text-4xl font-bold text-purple-600 mb-2">300,000円<span className="text-lg text-gray-600">(税込)</span></div>
              <p className="text-gray-700 leading-relaxed">協賛ボード最上段掲載/プログラム掲載/創始者との記念撮影/高級ギフト付</p>
            </div>
          </div>
        </div>
      </div>

      {/* Event Details */}
      <div className="py-20 px-4 bg-white/50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <img 
              src="https://dynavision.co.jp/30Anniversary/img/logo.png" 
              alt="ダイナビジョン" 
              className="w-32 mx-auto shiny mb-4"
            />
            <h3 className="text-2xl md:text-3xl font-bold text-gray-800">
              イベント詳細
            </h3>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-pink-500 to-purple-500">
                  <th colSpan={2} className="py-4 px-6 text-white text-xl font-bold">詳細情報</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <th className="py-4 px-6 bg-pink-50 text-left font-bold text-gray-800 w-1/3">開催日</th>
                  <td className="py-4 px-6 text-gray-700">2026年1月23日(金)</td>
                </tr>
                <tr className="border-b">
                  <th className="py-4 px-6 bg-pink-50 text-left font-bold text-gray-800">開始</th>
                  <td className="py-4 px-6 text-gray-700">17:00~<br />(受付開始 16時30分)</td>
                </tr>
                <tr className="border-b">
                  <th className="py-4 px-6 bg-pink-50 text-left font-bold text-gray-800">会場</th>
                  <td className="py-4 px-6 text-gray-700">ホテル インターコンチネンタル東京ベイ</td>
                </tr>
                <tr>
                  <th className="py-4 px-6 bg-pink-50 text-left font-bold text-gray-800">ドレスコード</th>
                  <td className="py-4 px-6 text-gray-700">フォーマル/セミフォーマルでお越しください</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* CTA Button 3 */}
      <div className="background-fixed">
        <div className="text-center">
          <a 
            href="https://dynavisionasp.com/p/campaign/xC1bmkhA/sQ5m5w2R" 
            target="_blank" 
            rel="noopener noreferrer"
            className="bouncy inline-block"
          >
            <img 
              src="https://dynavision.co.jp/30Anniversary/img/moushi.jpg" 
              alt="お申し込みはこちら" 
              className="w-4/5 max-w-md mx-auto"
            />
          </a>
        </div>
      </div>

      {/* Special Gifts */}
      <div className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-12">
            <img 
              src="https://dynavision.co.jp/30Anniversary/img/logo.png" 
              alt="ダイナビジョン" 
              className="w-32 mx-auto shiny mb-4"
            />
            <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-8">
              来場者特典
            </h3>
          </div>

          <div className="space-y-8 text-xl md:text-2xl text-gray-700 font-semibold">
            <p className="water-drop">✨ 参加者全員に、心を込めた特別ギフトをご用意</p>
            <p className="water-drop" style={{ animationDelay: '0.2s' }}>✨ 記念パンフレット・フォトスポット完備</p>
          </div>
        </div>
      </div>

      {/* CEO Message */}
      <div className="py-20 px-4 bg-white/50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white p-8 md:p-12 rounded-2xl shadow-lg">
            <div className="text-center mb-8">
              <img 
                src="https://dynavision.co.jp/30Anniversary/img/logo.png" 
                alt="ダイナビジョン" 
                className="w-32 mx-auto shiny mb-4"
              />
              <h3 className="text-2xl md:text-3xl font-bold text-gray-800">
                現代表・穂利辺ヒロよりご挨拶
              </h3>
            </div>

            <div className="text-center mb-8">
              <img 
                src="https://dynavision.co.jp/30Anniversary/img/hiro.png" 
                alt="穂利辺ヒロ" 
                className="w-full fade-in rounded-lg"
              />
            </div>

            <div className="text-left text-base md:text-lg leading-loose space-y-6">
              <p>皆さま、こんにちは。株式会社ダイナビジョン代表の 穂利辺ヒロ です。</p>
              <p>創始者・穴口恵子の"魂の種"から始まったこの会社は、30年をかけて"愛と共鳴の大樹"へと成長しました。</p>
              <p>今、私たちはその想いを次の世代へとつなぎ、<strong className="js-marker">「スピリチュアル × ビジネス × チーム」</strong>で新しい未来を創っています。</p>
              <p>このパーティーは、過去の感謝と、未来への宣言を込めた式典です。ここで生まれる共鳴が、また新たな光の循環を世界に広げていくことを信じています。</p>
              <p>皆さまと直接お会いし、共に笑い、祝福できることを心から楽しみにしています。</p>
            </div>

            <div className="text-right mt-8 text-gray-700">
              <p className="text-lg">株式会社ダイナビジョン 代表取締役</p>
              <p className="text-xl font-bold mt-2">穂利辺ヒロ</p>
            </div>
          </div>
        </div>
      </div>

      {/* Staff Message */}
      <div className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white p-8 md:p-12 rounded-2xl shadow-lg">
            <div className="text-center mb-8">
              <img 
                src="https://dynavision.co.jp/30Anniversary/img/logo.png" 
                alt="ダイナビジョン" 
                className="w-32 mx-auto shiny mb-4"
              />
              <h3 className="text-2xl md:text-3xl font-bold text-gray-800">
                ダイナビジョン スタッフ一同より
              </h3>
            </div>

            <div className="text-left text-base md:text-lg leading-loose space-y-6">
              <p>30年間、共に歩んでくださった皆さまへ。笑顔も涙も、挑戦も成長も──すべてが宝物です。この夜は、皆さま一人ひとりへの<strong className="js-marker">「ありがとう」</strong>を伝えるために、スタッフ全員が心を込めて準備しています。</p>
              <p>どうぞ、私たちと一緒に"愛の輪"を広げる時間をお楽しみください。</p>
            </div>

            <div className="text-right mt-8 text-gray-700">
              <p className="text-xl font-bold">ダイナビジョン スタッフ一同</p>
            </div>
          </div>

          {/* Final Message */}
          <div className="mt-16 text-center">
            <div className="mb-8">
              <img 
                src="https://dynavision.co.jp/30Anniversary/img/logo.png" 
                alt="ダイナビジョン" 
                className="w-32 mx-auto shiny mb-4"
              />
              <h3 className="text-2xl md:text-3xl font-bold text-gray-800">
                最後に
              </h3>
            </div>

            <div className="text-base md:text-lg leading-loose space-y-6 text-gray-700">
              <p>30年間の奇跡は、「あなた」という光があったからこそ。<br />このパーティーは、あなたへの<span className="js-marker">"感謝の祈り"</span>です。</p>
              <p>共に祝う150名の魂が共鳴し、未来が光り輝く夜に<br />ぜひ、あなたもその一人としてご参加ください。</p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Button 4 */}
      <div className="background-fixed">
        <div className="text-center">
          <a 
            href="https://dynavisionasp.com/p/campaign/xC1bmkhA/sQ5m5w2R" 
            target="_blank" 
            rel="noopener noreferrer"
            className="bouncy inline-block"
          >
            <img 
              src="https://dynavision.co.jp/30Anniversary/img/moushi.jpg" 
              alt="お申し込みはこちら" 
              className="w-4/5 max-w-md mx-auto"
            />
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-pink-100 to-purple-100 py-8 text-center text-gray-600">
        <p>Copyright © Dynavision Co .,Ltd. All Rights Reserved.</p>
      </footer>
    </div>
  );
}
